import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, FlexControlledForm, Preloader,
} from '@common-fe/common-fe';
import styled from 'styled-components';
import * as yup from 'yup';

import AppButton from '@/components/controls/AppButton';
import { PaymentMethodOwnerType, PaymentMethodType } from '@/modules/employee/employee.types';
import colors from '@/styles/colors';

import useAddPaymentMethodQuery from '../useAddPaymentMethod.query';
import { useUpdatePaymentMethodQuery } from '../useUpdatePaymentMethod.query';

import { useVenmoFields,VenmoInfo } from './hooks/useVemoFields';

const BUTTON_WIDTH = '150px';

const StyledBox = styled(Box)`
  ${({ theme }) => theme.border.blueGray2}
  ${({ theme }) => theme.shadows.default}
`;

interface Props {
  onBack?: () => void;
  onSuccess?: () => void;
  editableMethodId?: string;
  onError?: () => void;
}

export const AddVenmoForm: React.FC<Props> = ({
  onBack,
  onSuccess,
  editableMethodId,
  onError,
}) => {
  const { save, isLoading, isSuccess } = useAddPaymentMethodQuery(onSuccess);
  const {
    updatePaymentMethod,
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
  } = useUpdatePaymentMethodQuery(onSuccess);
  const fields = useVenmoFields();
  const [formValues, setFormValues] = useState<VenmoInfo>({});
  const [activeValues, setActiveValues] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const handleChangeValues = useCallback((values: VenmoInfo) => {
    setFormValues((prev) => ({ ...prev, ...values }));
    try {
      const validatorMap = fields.reduce((map, field) => ({
        ...map,
        [field.name]: field.validator,
      }), {});
      const schema = yup.object()
        .shape(validatorMap);
      schema.validateSync(values, { abortEarly: false });
      setActiveValues(true);
    } catch {
      setActiveValues(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.phoneNumber]);

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitted(true);
      if (activeValues && onSuccess) {
        if (editableMethodId) {
          await updatePaymentMethod({
            paymentMethodId: editableMethodId,
            paymentType: PaymentMethodType.VENMO,
            venmoData: formValues,
          });
        } else {
          await save({
            paymentType: PaymentMethodType.VENMO,
            paymentOwnerType: PaymentMethodOwnerType.EMPLOYEE,
            venmoData: formValues,
            isDefault: true,
          });
        }
      }
    } catch (e) {
      // @ts-ignore
      if (onError && e.response.status === 409) {
        onError();
      }
    }
  }, [onError, activeValues, onSuccess, editableMethodId, updatePaymentMethod, formValues, save]);

  useEffect(() => {
    if (onSuccess && (isUpdateSuccess || isSuccess)) onSuccess();
  }, [isSuccess, isUpdateSuccess, onSuccess]);

  return (
    <>
      <Box
        pad="spacing24"
        round="moduleRound"
        background={{ color: colors.module }}
      >
        <StyledBox
          round="container1Round"
          background={{ color: colors.canvas }}
        >
          <FlexControlledForm
            fields={fields}
            showError={submitted}
            editMode
            onChangeValues={handleChangeValues}
          />
        </StyledBox>
      </Box>
      <Box pad={{ top: 'medium' }} direction="row" align="center">
        <Box direction="row" justify="end" align="center" gap="spacing12" margin={{ left: 'auto' }}>
          <AppButton
            testId="Venmo_form_back"
            buttonType="secondary"
            type="button"
            onClick={onBack}
            width={BUTTON_WIDTH}
          >
            {editableMethodId ? 'Cancel' : 'Back'}
          </AppButton>
          <AppButton
            type="button"
            width={BUTTON_WIDTH}
            onClick={handleSubmit}
          >
            {(isLoading || isUpdateLoading) ? <Preloader color="white" /> : 'Submit'}
          </AppButton>
        </Box>
      </Box>
    </>
  );
};
